// Pallet-status labels. Colours live in the status tones (@/lib/statusTone,
// <StatusPill object="pallet"/>); the labels here must equal that map's.
//
// These maps were copy-pasted across Runs.tsx (two maps) and Pallets.tsx
// (STATUS_CONFIG). Keying off PalletStatus the colours/labels must agree
// everywhere a pallet badge renders — the shared PalletTable view needs
// them too — so they live here and the call sites import.
//
// Keyed by the raw status string (PalletStatus) so consumers that only
// have `status: string` (e.g. the Runs query) don't need to narrow first.

export const PALLET_STATUS_LABEL: Record<string, string> = {
  IN_PRODUCTION:      'In Production',
  IN_COLDROOM:        'In Coldroom',
  IN_LOADING_AREA:    'Loading Area',
  INSPECTED_APPROVED: 'Approved',
  INSPECTED_REJECTED: 'Rejected',
  DISPATCHED:         'Dispatched',
  RETURNED_FRESHMARK: 'Returned (Freshmark)',
}

export function palletStatusLabel(status: string): string {
  return PALLET_STATUS_LABEL[status] ?? status
}

// A RECEIVED pallet whose last cartons were stripped into a bin. Never
// placed, strip_received_pallet_to_bin soft-deletes it. Placed in a coldroom,
// it moves it to DISPATCHED instead — that stops the storage billing and frees
// the cell (mig 20261019090000). The pallet never left on a truck, so it must
// not read "Dispatched": a fully stripped pallet looked shipped and sent a
// packhouse looking for a load (1 Oct 2026).
//
// The marker is the strip's own status_history row ('Stripped N ctns → bin
// RBN-…'). It is written in the same transaction as the DISPATCHED transition,
// so its changed_at equals the pallet's dispatched_at (the state machine stamps
// NOW()). status_history can never be edited or deleted. The strip's
// update_reason is NOT a marker: a later move, SSCC fix or override overwrites
// it while the pallet stays DISPATCHED. An undispatch clears dispatched_at, and
// a later real dispatch stamps a new one, so neither reads as retipped.
export const STRIP_REASON_PREFIX = 'Stripped '

export function isRetippedDispatch(
  p: { status: string; dispatched_at?: string | null },
  stripTimes: readonly string[] | undefined,
): boolean {
  if (p.status !== 'DISPATCHED' || !p.dispatched_at || !stripTimes?.length) return false
  const at = Date.parse(p.dispatched_at)
  return stripTimes.some(t => Date.parse(t) === at)
}

// The status word for ONE pallet: "Retipped" for a fully stripped one, the
// plain status label otherwise.
export function palletStateLabel(
  p: { status: string; dispatched_at?: string | null },
  stripTimes: readonly string[] | undefined,
): string {
  return isRetippedDispatch(p, stripTimes) ? 'Retipped' : palletStatusLabel(p.status)
}
